import { motion, useScroll, useTransform, useReducedMotion, useMotionValueEvent } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { ArrowRightIcon } from '@heroicons/react/24/outline'

interface NavbarProps {
  variant?: 'light' | 'dark'
}

const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]

export function Navbar({ variant = 'dark' }: NavbarProps) {
  const light = variant === 'light'
  const reduced = useReducedMotion()
  const { scrollY } = useScroll()
  const [scrolled, setScrolled] = useState(false)

  useMotionValueEvent(scrollY, 'change', (v) => {
    setScrolled(v > 60)
  })

  return (
    <motion.nav
      initial={reduced ? undefined : { opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease }}
      className="fixed left-0 right-0 top-0 z-50 flex justify-center px-4 py-4 sm:px-8"
    >
      <div
        className={[
          'relative flex w-full items-center border backdrop-blur-2xl will-change-transform',
          'transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
          light ? 'bg-[#eee8dc]/90 border-[#211d18]/8' : 'bg-[#211d18]/92 border-[#eee8dc]/[0.06]',
          scrolled ? 'max-w-[200px] rounded-full px-5 py-2.5' : 'max-w-[1480px] rounded-2xl px-6 py-3',
        ].join(' ')}
      >
        {/* Brand — absolute center, never moves */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Link
            to="/"
            className={[
              'font-serif pointer-events-auto transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
              light ? 'text-[#211d18] hover:text-[#6e1f1b]' : 'text-[#eee8dc] hover:text-[#c8a88a]',
              scrolled ? 'text-[14px]' : light ? 'text-[22px]' : 'text-[17px]',
            ].join(' ')}
          >
            Jpdz Labs
          </Link>
        </div>

        {/* Left */}
        <div
          className={[
            'flex items-center gap-8 overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
            scrolled ? 'max-w-0 opacity-0' : 'max-w-[600px] opacity-100',
          ].join(' ')}
        >
          {light ? (
            <div className="hidden items-center gap-8 md:flex">
              <Link to="/about" className="whitespace-nowrap font-body text-[13px] text-[#6b6157] transition-colors hover:text-[#211d18]">
                About
              </Link>
              <Link to="/developers" className="whitespace-nowrap font-body text-[13px] text-[#6b6157] transition-colors hover:text-[#211d18]">
                Developers
              </Link>
            </div>
          ) : (
            <Link to="/" className="inline-flex min-h-[44px] items-center whitespace-nowrap text-[#eee8dc]/50 transition-colors duration-200 hover:text-[#eee8dc]">
              <span className="font-body text-[13px]">Home</span>
            </Link>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right */}
        <div
          className={[
            'flex items-center gap-4 overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
            scrolled ? 'max-w-0 opacity-0' : 'max-w-[600px] opacity-100',
          ].join(' ')}
        >
          {light ? (
            <>
              <Link
                to="/chat"
                className="hidden whitespace-nowrap items-center gap-2 border border-[#211d18] rounded-xl px-5 py-2.5 font-body text-[13px] font-semibold text-[#211d18] transition-all duration-300 hover:bg-[#211d18] hover:text-[#eee8dc] md:inline-flex"
              >
                Open Jpdz
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/chat"
                className="inline-flex min-h-11 whitespace-nowrap items-center border border-[#211d18] rounded-xl px-4 font-body text-sm font-semibold text-[#211d18] transition-all duration-300 hover:bg-[#211d18] hover:text-[#eee8dc] md:hidden"
              >
                Open
              </Link>
            </>
          ) : (
            <Link to="/chat" className="inline-flex min-h-[44px] whitespace-nowrap items-center gap-2 px-4 font-body text-[12px] text-[#eee8dc]/50 transition-colors duration-200 hover:text-[#eee8dc]">
              Open Jpdz <ArrowRightIcon className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>
    </motion.nav>
  )
}
